

/**
 * プロジェクト一覧ページ
 * プロジェクトの一覧表示と検索・フィルタリング
 */

import { AdminPageWrapper, PageLayout } from '@/shared/components'
import { Badge } from "@/shared/ui/badge"
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card"
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui/content-states'
import { DatePickerWithRange } from '@/shared/ui/date-picker-with-range'
import { Input } from '@/shared/ui/input'
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/shared/ui/pagination'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select"
import { Skeleton } from '@/shared/ui/skeleton'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/shared/ui/table'
import { NewProjectModal } from '@/features/projects/components/NewProjectModal'
import { useProjects } from '@/features/projects/hooks/useProjects'
import { BuildingType, Project, ProjectStatus } from '@/features/projects/types'
import { ArrowRight, ListFilter, Search, SlidersHorizontal, X } from "lucide-react"
import { Link } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'


const ITEMS_PER_PAGE = 15

const ALL_STATUSES_VALUE = 'ALL_STATUSES'
const ALL_BUILDING_TYPES_VALUE = 'ALL_BUILDING_TYPES'

/**
 * プロジェクトフィルター用の型定義
 */
interface ProjectFilters {
    name?: string
    status?: ProjectStatus
    building_type?: BuildingType
    start_date_from?: string
    start_date_to?: string
    end_date_from?: string
    end_date_to?: string
    area_min?: number
    area_max?: number
    limit: number
    skip: number
}

/**
 * プロジェクトフィルター入力の型定義
 */
interface ProjectFilterInputs {
    nameFilterInput: string
    statusFilterInput: ProjectStatus | typeof ALL_STATUSES_VALUE
    buildingTypeFilterInput: BuildingType | typeof ALL_BUILDING_TYPES_VALUE
    startDateRange: DateRange | undefined
    endDateRange: DateRange | undefined
    areaMinInput: string
    areaMaxInput: string
    currentPage: number
}

/**
 * プロジェクトフィルター状態管理カスタムフック
 */
function useProjectFilters() {
    const [inputs, setInputs] = useState<ProjectFilterInputs>({
        nameFilterInput: '',
        statusFilterInput: ALL_STATUSES_VALUE,
        buildingTypeFilterInput: ALL_BUILDING_TYPES_VALUE,
        startDateRange: undefined,
        endDateRange: undefined,
        areaMinInput: '',
        areaMaxInput: '',
        currentPage: 1,
    })

    const [filters, setFilters] = useState<ProjectFilters>({
        limit: ITEMS_PER_PAGE,
        skip: 0
    })

    const applyFilters = useCallback(() => {
        const newFilters: ProjectFilters = {
            name: inputs.nameFilterInput || undefined,
            status: inputs.statusFilterInput === ALL_STATUSES_VALUE ? undefined : inputs.statusFilterInput,
            building_type: inputs.buildingTypeFilterInput === ALL_BUILDING_TYPES_VALUE ? undefined : inputs.buildingTypeFilterInput,
            start_date_from: inputs.startDateRange?.from ? inputs.startDateRange.from.toISOString().split('T')[0] : undefined,
            start_date_to: inputs.startDateRange?.to ? inputs.startDateRange.to.toISOString().split('T')[0] : undefined,
            end_date_from: inputs.endDateRange?.from ? inputs.endDateRange.from.toISOString().split('T')[0] : undefined,
            end_date_to: inputs.endDateRange?.to ? inputs.endDateRange.to.toISOString().split('T')[0] : undefined,
            area_min: inputs.areaMinInput ? parseInt(inputs.areaMinInput, 10) : undefined,
            area_max: inputs.areaMaxInput ? parseInt(inputs.areaMaxInput, 10) : undefined,
            limit: ITEMS_PER_PAGE,
            skip: 0
        }
        setFilters(newFilters)
        setInputs(prev => ({ ...prev, currentPage: 1 }))
    }, [inputs])

    const clearFilters = useCallback(() => {
        const clearedInputs: ProjectFilterInputs = {
            nameFilterInput: '',
            statusFilterInput: ALL_STATUSES_VALUE,
            buildingTypeFilterInput: ALL_BUILDING_TYPES_VALUE,
            startDateRange: undefined,
            endDateRange: undefined,
            areaMinInput: '',
            areaMaxInput: '',
            currentPage: 1,
        }
        setInputs(clearedInputs)
        setFilters({ limit: ITEMS_PER_PAGE, skip: 0 })
    }, [])

    const changePage = useCallback((newPage: number) => {
        setInputs(prev => ({ ...prev, currentPage: newPage }))
        setFilters(prev => ({
            ...prev,
            skip: (newPage - 1) * ITEMS_PER_PAGE
        }))
    }, [])

    const updateInputs = useCallback((updates: Partial<ProjectFilterInputs>) => {
        setInputs(prev => ({ ...prev, ...updates }))
    }, [])

    useEffect(() => {
        applyFilters()
    }, [])

    return {
        inputs,
        filters,
        updateInputs,
        applyFilters,
        clearFilters,
        changePage,
    }
}

/**
 * ステータスオプション
 */
const STATUS_OPTIONS: { value: ProjectStatus | typeof ALL_STATUSES_VALUE; label: string }[] = [
    { value: ALL_STATUSES_VALUE, label: 'すべて' },
    { value: '進行中', label: '進行中' },
    { value: '完了', label: '完了' },
    { value: '保留', label: '保留' },
    { value: '中止', label: '中止' },
]

/**
 * 建築種別オプション
 */
const BUILDING_TYPE_OPTIONS: { value: BuildingType | typeof ALL_BUILDING_TYPES_VALUE; label: string }[] = [
    { value: ALL_BUILDING_TYPES_VALUE, label: 'すべて' },
    { value: '商業施設', label: '商業施設' },
    { value: '住宅', label: '住宅' },
    { value: 'オフィス', label: 'オフィス' },
    { value: '分譲マンション', label: '分譲マンション' },
    { value: '戸建住宅', label: '戸建住宅' },
    { value: '工場・倉庫', label: '工場・倉庫' },
]

/**
 * 日付フォーマット関数
 */
const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return <span className="text-muted-foreground">-</span>
    try {
        return new Date(dateString).toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
        return <span className="text-destructive">日付エラー</span>
    }
}

/**
 * ステータスバッジ取得関数
 */
const getStatusBadge = (status: ProjectStatus | null | undefined) => {
    switch (status) {
        case '進行中':
            return <Badge variant="default" className="bg-sky-500 hover:bg-sky-600 text-sky-50 whitespace-nowrap">進行中</Badge>
        case '完了':
            return <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-green-50 whitespace-nowrap">完了</Badge>
        case '保留':
            return <Badge variant="outline" className="border-yellow-500 text-yellow-600 hover:bg-yellow-50 whitespace-nowrap">保留</Badge>
        case '中止':
            return <Badge variant="secondary" className="whitespace-nowrap">中止</Badge>
        default:
            return <Badge variant="outline" className="whitespace-nowrap">不明</Badge>
    }
}


/**
 * プロジェクトリストスケルトンコンポーネント
 */
function ProjectListSkeleton() {
    return (
        <div className="space-y-3">
            {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
                <div key={i} className="grid grid-cols-5 gap-4 items-center p-3 border-b last:border-b-0">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-5 w-1/2" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-5 w-1/3" />
                    <Skeleton className="h-5 w-1/3" />
                </div>
            ))}
        </div>
    )
}

/**
 * プロジェクトページコンテンツコンポーネント
 */
function ProjectsPageContent() {
    const {
        inputs: {
            nameFilterInput,
            statusFilterInput,
            buildingTypeFilterInput,
            startDateRange,
            endDateRange,
            areaMinInput,
            areaMaxInput,
            currentPage
        },
        filters,
        updateInputs,
        applyFilters,
        clearFilters,
        changePage
    } = useProjectFilters()

    const { projects, isLoading, error, total } = useProjects({
        limit: filters.limit,
        skip: filters.skip,
        status: (filters.status as any) === 'ALL_STATUSES' ? undefined : filters.status,
        name: filters.name,
        building_type: filters.building_type,
        start_date_from: filters.start_date_from,
        start_date_to: filters.start_date_to,
        end_date_from: filters.end_date_from,
        end_date_to: filters.end_date_to,
        area_min: filters.area_min,
        area_max: filters.area_max,
    })
    const totalCount = total

    if (error) {
        return (
            <PageLayout
                title="プロジェクト一覧"
                description="進行中および過去のプロジェクトを検索・管理する。"
            >
                <ErrorState
                    title="プロジェクト一覧の読み込みに失敗しました"
                    message={String(error)}
                    onRetry={() => window.location.reload()}
                />
            </PageLayout>
        )
    }

    const pageCount = Math.ceil((totalCount ?? 0) / ITEMS_PER_PAGE)

    return (
        <PageLayout
            title="プロジェクト一覧"
            description="進行中および過去のプロジェクトを検索・管理する。"
            actions={
                <NewProjectModal>
                    <Button>新規プロジェクト作成</Button>
                </NewProjectModal>
            }
        >
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center">
                        <SlidersHorizontal className="mr-2 h-5 w-5" />
                        フィルター
                    </CardTitle>
                    <CardDescription>
                        物件名、ステータス、建築種別、プロジェクト開始日・終了日、合計平米数で表示するプロジェクトを絞り込めます。
                    </CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 items-end">
                    <div className="space-y-1.5">
                        <label htmlFor="name-filter" className="text-sm font-medium">物件名</label>
                        <Input
                            id="name-filter"
                            placeholder="例: 山田邸新築工事"
                            value={nameFilterInput}
                            onChange={(e) => updateInputs({ nameFilterInput: e.target.value })}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="status-filter" className="text-sm font-medium">ステータス</label>
                        <Select value={statusFilterInput} onValueChange={(value) => updateInputs({ statusFilterInput: value as ProjectStatus | typeof ALL_STATUSES_VALUE })}>
                            <SelectTrigger id="status-filter" className="w-full">
                                <SelectValue placeholder="ステータスを選択" />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="building-type-filter" className="text-sm font-medium">建築種別</label>
                        <Select value={buildingTypeFilterInput} onValueChange={(value) => updateInputs({ buildingTypeFilterInput: value as BuildingType | typeof ALL_BUILDING_TYPES_VALUE })}>
                            <SelectTrigger id="building-type-filter" className="w-full">
                                <SelectValue placeholder="建築種別を選択" />
                            </SelectTrigger>
                            <SelectContent>
                                {BUILDING_TYPE_OPTIONS.map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="start-date-filter" className="text-sm font-medium">プロジェクト開始日</label>
                        <DatePickerWithRange
                            id="start-date-filter"
                            dateRange={startDateRange}
                            onDateChange={(range) => updateInputs({ startDateRange: range })}
                            className="w-full"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="end-date-filter" className="text-sm font-medium">プロジェクト終了日</label>
                        <DatePickerWithRange
                            id="end-date-filter"
                            dateRange={endDateRange}
                            onDateChange={(range) => updateInputs({ endDateRange: range })}
                            className="w-full"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="area-min-filter" className="text-sm font-medium">最小平米数</label>
                        <Input
                            id="area-min-filter"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="例: 100"
                            value={areaMinInput}
                            onChange={(e) => updateInputs({ areaMinInput: e.target.value })}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label htmlFor="area-max-filter" className="text-sm font-medium">最大平米数</label>
                        <Input
                            id="area-max-filter"
                            type="number"
                            min="0"
                            step="1"
                            placeholder="例: 500"
                            value={areaMaxInput}
                            onChange={(e) => updateInputs({ areaMaxInput: e.target.value })}
                            className="w-full"
                        />
                    </div>
                    <div className="flex gap-2 items-end pt-4 sm:pt-0 xl:col-span-1 justify-start xl:justify-end">
                        <Button onClick={applyFilters} disabled={isLoading} className="w-full md:w-auto">
                            <Search className="mr-2 h-4 w-4" />
                            適用
                        </Button>
                        <Button variant="outline" onClick={clearFilters} disabled={isLoading} className="w-full md:w-auto">
                            <X className="mr-2 h-4 w-4" />
                            クリア
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <div className="mt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>検索結果</CardTitle>
                        <CardDescription>
                            全 {totalCount ?? 0} 件のプロジェクトが見つかりました。
                            {filters?.name && <span className="ml-2">物件名:「{filters.name}」</span>}
                            {filters?.status && <span className="ml-2">ステータス:「{STATUS_OPTIONS.find(s => s.value === filters.status)?.label}」</span>}
                            {filters?.building_type && <span className="ml-2">建築種別:「{BUILDING_TYPE_OPTIONS.find(b => b.value === filters.building_type)?.label}」</span>}
                            {(filters?.start_date_from || filters?.start_date_to) && (
                                <span className="ml-2">
                                    開始日:「{filters.start_date_from || '未指定'} ～ {filters.start_date_to || '未指定'}」
                                </span>
                            )}
                            {(filters?.end_date_from || filters?.end_date_to) && (
                                <span className="ml-2">
                                    終了日:「{filters.end_date_from || '未指定'} ～ {filters.end_date_to || '未指定'}」
                                </span>
                            )}
                            {(filters?.area_min || filters?.area_max) && (
                                <span className="ml-2">
                                    平米数:「{filters.area_min || '未指定'} ～ {filters.area_max || '未指定'}」
                                </span>
                            )}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <LoadingState message="プロジェクト一覧を読み込んでいます..." />
                        ) : projects && projects.length > 0 ? (
                            <Table data-testid="projects-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="min-w-[200px]">物件名</TableHead>
                                        <TableHead className="min-w-[120px]">ステータス</TableHead>
                                        <TableHead className="min-w-[250px]">住所</TableHead>
                                        <TableHead className="min-w-[100px]">開始日</TableHead>
                                        <TableHead className="min-w-[100px]">終了日</TableHead>
                                        <TableHead className="min-w-[60px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {projects.map((project: Project) => (
                                        <TableRow key={project.id}>
                                            <TableCell className="font-medium">
                                                <Link to={`/projects/${project.id}`} className="hover:underline text-primary">
                                                    {project.name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{getStatusBadge(project.status as ProjectStatus | null | undefined)}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{project.address ?? '-'}</TableCell>
                                            <TableCell className="text-sm">{formatDate(project.start_date)}</TableCell>
                                            <TableCell className="text-sm">{formatDate(project.end_date)}</TableCell>
                                            <TableCell>
                                                <Link to={`/projects/${project.id}`}>
                                                    <Button variant="outline" size="sm" aria-label="詳細を見る">
                                                        <ArrowRight className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <EmptyState
                                icon={<ListFilter className="h-16 w-16" />}
                                title="該当するプロジェクトは見つかりませんでした"
                                message="フィルター条件を変更して再度お試しください。"
                                action={{
                                    label: "フィルターをクリア",
                                    onClick: clearFilters
                                }}
                            />
                        )}
                    </CardContent>
                    {projects && projects.length > 0 && pageCount > 1 && (
                        <div className="flex justify-center py-4 border-t">
                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); if (currentPage > 1) changePage(currentPage - 1); }}
                                            aria-disabled={currentPage <= 1}
                                            className={currentPage <= 1 ? "pointer-events-none opacity-50" : undefined}
                                        />
                                    </PaginationItem>
                                    {[...Array(pageCount)].map((_, i) => (
                                        <PaginationItem key={i + 1}>
                                            <PaginationLink
                                                href="#"
                                                onClick={(e) => { e.preventDefault(); changePage(i + 1); }}
                                                isActive={currentPage === i + 1}
                                            >
                                                {i + 1}
                                            </PaginationLink>
                                        </PaginationItem>
                                    ))}
                                    <PaginationItem>
                                        <PaginationNext
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); if (currentPage < pageCount) changePage(currentPage + 1); }}
                                            aria-disabled={currentPage >= pageCount}
                                            className={currentPage >= pageCount ? "pointer-events-none opacity-50" : undefined}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    )}
                </Card>
            </div>
        </PageLayout>
    )
}

/**
 * プロジェクトページコンポーネント
 */
export default function ProjectsPage() {
    return (
        <AdminPageWrapper>
            <ProjectsPageContent />
        </AdminPageWrapper>
    )
} 